'use client';

import { analytics } from 'lib/analytics/analytics';
import { ShippingBox } from 'assets/icons';
import {
  MidPanel,
  MidPanelPhone,
  LeftPanel,
  RightPanel,
} from 'components/shared/BottomBar';
import { useContext } from 'react';
import { useFormatPrice } from 'hooks/pricing/useFormattedPrice';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { DotSpinner, useModal } from '../..';
import styles from './BottomBar.module.css';
import { useCartStore, useShallow } from 'components/shared/Cart';
import { DieselRingSelectionContext } from './DieselRingSelectionContext';
import { PrimaryButton } from 'components/shared/PrimaryButton';
import { productMap_diesel_ring } from 'content/products/dieselRing';

export const BottomBar = ({ className }: { className?: string }) => {
  const RingSelectionClient = useContext(DieselRingSelectionContext);
  const CartClient = useCartStore(
    useShallow((s) => ({ store: s.store, taxString: s.taxString })),
  );
  const ModalClient = useModal();
  const formatPriceWithComma = useFormatPrice();

  const {
    size,
    uhx,
    uhxCart,
    shippingEstimate,
    color,
    defaultCurrency: currency,
    notServicable,
    outOfStock,
    goToNextStep,
    isLoading,
  } = RingSelectionClient;

  const handleCheckout = () => {
    if (RingSelectionClient.size) {
      RingSelectionClient.size
        ? analytics.track(`Bottom bar addto cart - CLICK`)
        : analytics.track(`Bottom bar select color-size - CLICK`);
    }
    openCheckoutInfo();
  };

  const openCheckoutInfo = () => {
    handleProceedClick();
    return;
  };

  const handleProceedClick = () => {
    if (isLoading) return;
    analytics.track('ring-air-checkout', {
      clicked: true,
    });
    RingSelectionClient.checkout();
    ModalClient.hide();
  };

  const sctollToOrderSummary = () => {
    const ref = document.getElementById('order-summary-card');
    if (ref) ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const { store } = CartClient;

  const MidPriceSection = (
    <>
      <MidPanel>
        <p className="total-price-single">
          {RingSelectionClient.currencySymbol}
          {formatPriceWithComma(RingSelectionClient.effectivePrice)}
          <span className="tax-disclaimer">({CartClient.taxString})</span>
        </p>
      </MidPanel>

      <MidPanelPhone onClick={sctollToOrderSummary}>
        <div className="subtitle-text-btn">Total</div>
        <p className="total-price-single">
          {RingSelectionClient.currencySymbol}
          {formatPriceWithComma(RingSelectionClient.effectivePrice)}
          <span className="tax-disclaimer">({CartClient.taxString})</span>
        </p>
      </MidPanelPhone>
    </>
  );

  if (notServicable === true || outOfStock === true) return null;

  return (
    <div
      className={`${styles.root} u-grid${className ? ` ${className}` : ''}`}
      data-bottom-bar
    >
      <LeftPanel>
        {shippingEstimate ? (
          <p className="chat-specialist">
            <ShippingBox /> {shippingEstimate}
          </p>
        ) : null}
      </LeftPanel>

      {RingSelectionClient.size ? MidPriceSection : null}

      <RightPanel>
        {!!size && (uhx || uhxCart !== null) ? (
          <PrimaryButton
            onClick={handleCheckout}
            disabled={isLoading || !(!!size && (uhx || uhxCart !== null))}
            aria-busy={isLoading}
            data-buttontype="add to cart"
            data-variantId={
              color && size && currency
                ? productMap_diesel_ring[color]?.[size]?.NOT_ENGRAVED.variantId[
                    store as ShopifyStore
                  ]
                : ''
            }
            data-product="Diesel Ultrahuman Ring"
          >
            {isLoading ? (
              <>
                <DotSpinner color="#000000" />
              </>
            ) : (
              <>
                {RingSelectionClient.size
                  ? 'Add to cart'
                  : 'Please select a color & size'}
              </>
            )}
          </PrimaryButton>
        ) : (
          <PrimaryButton
            onClick={goToNextStep}
            data-buttontype="go to next step"
            data-variantId={
              color && size && currency
                ? productMap_diesel_ring[color]?.[size]?.NOT_ENGRAVED
                    ?.variantId[store as ShopifyStore]
                : ''
            }
            data-product="Diesel Ultrahuman Ring"
          >
            Continue
          </PrimaryButton>
        )}
      </RightPanel>
    </div>
  );
};
